import { useState } from 'react';

export default function GalleryLightbox({ images, showTitles = true }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Normaliser billeder til en ensartet struktur: { url, name }
  const normalizedImages = (images || [])
    .map((img, index) => {
      if (!img) return null;
      if (typeof img === 'string') {
        return { url: img, name: '' };
      }
      return {
        url: img.url || (Array.isArray(img.image) ? img.image[0] : img.image) || '',
        name: img.name || img.title || '',
      };
    })
    .filter((img) => img && img.url);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % normalizedImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + normalizedImages.length) % normalizedImages.length);
  };

  if (normalizedImages.length === 0) {
    return null;
  }

  const currentImage = lightboxIndex !== null ? normalizedImages[lightboxIndex] : null;

  return (
    <div>
      {/* Grid of images */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {normalizedImages.map((img, index) => {
          const cleanPath = (img.url || '')
            .replace(/^https?:\/\/assets\.auxodev\.dk\//, '')
            .replace(/^:?\/\/assets\.auxodev\.dk\//, '');
          const thumbUrl = `https://assets.auxodev.dk/cdn-cgi/image/width=360,height=360,fit=cover,quality=80,format=auto/${cleanPath}`;

          return (
            <div
              key={index}
              onClick={() => openLightbox(index)}
              className="group flex flex-col bg-white rounded-2xl overflow-hidden shadow-xs hover:shadow-md border border-cream-200 cursor-pointer transition-all duration-300"
            >
              <div className="aspect-square relative overflow-hidden bg-cream-100">
                <img
                  src={thumbUrl}
                  alt={img.name || `Galleri billede ${index + 1}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-white/95 text-forest-950 flex items-center justify-center shadow-md transform scale-90 group-hover:scale-100 transition-transform">
                    <i className="fa-solid fa-magnifying-glass-plus" />
                  </div>
                </div>
              </div>

              {/* Billedtitel / navn hvis til stede */}
              {showTitles && img.name ? (
                <div className="p-2.5 bg-white text-center border-t border-cream-100">
                  <p className="text-xs sm:text-sm font-serif font-semibold text-forest-950 truncate" title={img.name}>
                    {img.name}
                  </p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && currentImage && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 text-white/80 hover:text-white text-3xl focus:outline-none z-50 p-2 cursor-pointer transition-colors"
            aria-label="Luk visning"
          >
            <i className="fa-solid fa-xmark" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={prevImage}
            className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 text-white hover:text-[#9b4d85] text-2xl sm:text-3xl focus:outline-none z-50 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-black/70 rounded-full backdrop-blur-sm cursor-pointer transition-all"
            aria-label="Forrige billede"
          >
            <i className="fa-solid fa-chevron-left" />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 text-white hover:text-[#9b4d85] text-2xl sm:text-3xl focus:outline-none z-50 w-12 h-12 flex items-center justify-center bg-black/40 hover:bg-black/70 rounded-full backdrop-blur-sm cursor-pointer transition-all"
            aria-label="Næste billede"
          >
            <i className="fa-solid fa-chevron-right" />
          </button>

          {/* Image Container */}
          <div
            className="max-w-5xl max-h-[90vh] flex flex-col items-center justify-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1400,quality=90,format=auto/${(currentImage.url || '').replace(/^https?:\/\/assets\.auxodev\.dk\//, '').replace(/^:?\/\/assets\.auxodev\.dk\//, '')}`}
              alt={currentImage.name || `Galleri billede ${lightboxIndex + 1}`}
              className="max-w-full max-h-[75vh] object-contain rounded-xl shadow-2xl"
            />

            {/* Titel og billedtæller */}
            <div className="mt-4 text-center">
              {currentImage.name && (
                <h3 className="text-white font-serif font-bold text-lg sm:text-xl mb-1">
                  {currentImage.name}
                </h3>
              )}
              <span className="text-white/60 text-xs sm:text-sm font-medium">
                Billede {lightboxIndex + 1} af {normalizedImages.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
